/** Every role an account can hold. */
export const ROLES = [
  'patient',
  'doctor',
  'practice_admin',
  'front_desk',
  'pharmacist',
  'platform_admin',
] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/** Kinds of practice that can join Curentia. */
export const PRACTICE_TYPES = ['solo', 'clinic', 'small_hospital', 'pharmacy'] as const;
export type PracticeType = (typeof PRACTICE_TYPES)[number];

/**
 * Where a practice keeps its records. Clinics and small hospitals use an
 * always-on hub PC so every member sees the same records. Solo doctors and
 * single-counter pharmacies keep records on their own device.
 */
export type RecordHolder = 'device' | 'hub';

export const DEFAULT_RECORD_HOLDER: Readonly<Record<PracticeType, RecordHolder>> = {
  solo: 'device',
  clinic: 'hub',
  small_hospital: 'hub',
  pharmacy: 'device',
};

/** Roles a member of each practice type may hold. One person may hold several. */
export const PRACTICE_ROLES: Readonly<Record<PracticeType, readonly Role[]>> = {
  solo: ['doctor', 'practice_admin'],
  clinic: ['doctor', 'practice_admin', 'front_desk', 'pharmacist'],
  small_hospital: ['doctor', 'practice_admin', 'front_desk', 'pharmacist'],
  pharmacy: ['pharmacist', 'practice_admin'],
};

/** Roles whose professional registration the platform admin must verify. */
export const VERIFIED_ROLES: readonly Role[] = ['doctor', 'pharmacist'];

/** Kinds of data a practice holds, used to decide what each member may read. */
export const DATA_CATEGORIES = [
  'demographics',
  'clinical',
  'prescriptions',
  'dispensing',
  'membership',
] as const;
export type DataCategory = (typeof DATA_CATEGORIES)[number];

/**
 * What each practice role may read from the practice's records. The record
 * holder (hub or solo device) enforces this before sending anything. Patients
 * are not listed: they always see their own records and nothing else. The
 * platform admin sees no practice data at all.
 */
const ROLE_ACCESS: Readonly<Record<Exclude<Role, 'patient'>, readonly DataCategory[]>> = {
  doctor: ['demographics', 'clinical', 'prescriptions', 'dispensing'],
  practice_admin: ['membership'],
  front_desk: ['demographics'],
  pharmacist: ['demographics', 'prescriptions', 'dispensing'],
  platform_admin: [],
};

/** True when a member holding these roles may read this category of practice data. */
export function canAccess(roles: readonly Role[], category: DataCategory): boolean {
  return roles.some(
    (role) => role !== 'patient' && ROLE_ACCESS[role].includes(category),
  );
}

/**
 * What each practice role may write at the record holder. Front desk staff
 * register patients, doctors write clinical records and prescriptions, and
 * pharmacists record dispensing. Nobody writes membership: the server keeps it.
 */
const ROLE_WRITE: Readonly<Record<Exclude<Role, 'patient'>, readonly DataCategory[]>> = {
  doctor: ['demographics', 'clinical', 'prescriptions'],
  practice_admin: [],
  front_desk: ['demographics'],
  pharmacist: ['dispensing'],
  platform_admin: [],
};

/** True when a member holding these roles may write this category of practice data. */
export function canWrite(roles: readonly Role[], category: DataCategory): boolean {
  return roles.some((role) => role !== 'patient' && ROLE_WRITE[role].includes(category));
}

/** True when the role list is valid for a member of this practice type. */
export function rolesAllowedIn(type: PracticeType, roles: readonly Role[]): boolean {
  return roles.length > 0 && roles.every((role) => PRACTICE_ROLES[type].includes(role));
}
